import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { EntitlementsProvider } from './hooks/useEntitlements';
import { captureAcquisition } from './lib/acquisition';
import { track, routePattern } from './lib/appEvents';
import BottomTabBar from './components/BottomTabBar';
import InstallPrompt from './components/InstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';

// Eagerly loaded — core pages users hit immediately
import AuthPage from './pages/AuthPage';
import LandingPage from './pages/LandingPage';
import ChatPage from './pages/ChatPage';
import HomePage from './pages/HomePage';

// Lazy-loaded — feature modules loaded on demand
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const BookmarksPage = lazy(() => import('./pages/BookmarksPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminUserDetailPage = lazy(() => import('./pages/AdminUserDetailPage'));
const AdminAthleteProfilePage = lazy(() => import('./pages/AdminAthleteProfilePage'));
const AdminAthleteModelPage = lazy(() => import('./pages/AdminAthleteModelPage'));
const AdminEvaluationsPage = lazy(() => import('./pages/AdminEvaluationsPage'));
const AdminEngineSessionsPage = lazy(() => import('./pages/AdminEngineSessionsPage'));
const AdminEngineSessionDetailPage = lazy(() => import('./pages/AdminEngineSessionDetailPage'));
const AdminEngineResequencePage = lazy(() => import('./pages/AdminEngineResequencePage'));
const AdminChatPage = lazy(() => import('./pages/AdminChatPage'));
const AdminUserTimelinePage = lazy(() => import('./pages/AdminUserTimelinePage'));
const AdminActivityFeedPage = lazy(() => import('./pages/AdminActivityFeedPage'));
const AdminOpsPage = lazy(() => import('./pages/AdminOpsPage'));
const AdminResearchPage = lazy(() => import('./pages/AdminResearchPage'));
const AdminOutreachPage = lazy(() => import('./pages/AdminOutreachPage'));
const AdminRatingsPage = lazy(() => import('./pages/AdminRatingsPage'));
const AdminReportsPage = lazy(() => import('./pages/AdminReportsPage'));
const AdminWorkoutLogsPage = lazy(() => import('./pages/AdminWorkoutLogsPage'));
const AdminWorkoutLogDetailPage = lazy(() => import('./pages/AdminWorkoutLogDetailPage'));
const AdminProgramsPage = lazy(() => import('./pages/AdminProgramsPage'));
const AdminNutritionPage = lazy(() => import('./pages/AdminNutritionPage'));
const AdminProgramScorePage = lazy(() => import('./pages/AdminProgramScorePage'));
const AdminSignaturesPage = lazy(() => import('./pages/AdminSignaturesPage'));
const AthletePage = lazy(() => import('./pages/AthletePage'));
const AthleteDataPage = lazy(() => import('./pages/AthleteDataPage'));
const WorkoutReviewPage = lazy(() => import('./pages/WorkoutReviewPage'));
const StartWorkoutPage = lazy(() => import('./pages/StartWorkoutPage'));
const DayPage = lazy(() => import('./pages/DayPage'));
const TrainingLogPage = lazy(() => import('./pages/TrainingLogPage'));
const LogActivityPage = lazy(() => import('./pages/LogActivityPage'));
const WorkoutAnalysisPage = lazy(() => import('./pages/WorkoutAnalysisPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutCompletePage = lazy(() => import('./pages/CheckoutCompletePage'));
const CheckoutRedirectPage = lazy(() => import('./pages/CheckoutRedirectPage'));
// REMOVED (Decision 11 class sweep): the Engine Class pages (F4/F5 leaderboard/TV/view,
// JoinEnginePage) are deleted — Engine Class is not a product. REMOVED (Decision 12a,
// Phase C): ClaimSeatPage + the claim flow — gym members live on their gym's member app
// and never hold a wodwisdom account. Both old link shapes get the dead-link stub below.

// A retired gym link (the pre-Decision-11 engine-join flow and the pre-Decision-12a
// /claim/:token seat claim). Members now sign into their GYM's member app — the gym
// issues that login link from its portal roster.
function RetiredInviteNotice() {
  return (
    <div style={{ maxWidth: 440, margin: '0 auto', padding: '2rem 1.25rem' }}>
      <h1 style={{ fontSize: 22, margin: '0 0 0.5rem' }}>This link has been replaced</h1>
      <p style={{ opacity: 0.8 }}>
        Your gym now has its own member app. Ask your gym for a fresh app login link —
        they can issue one from their dashboard.
      </p>
    </div>
  );
}

// Programs
const ProgramsListPage = lazy(() => import('./pages/ProgramsListPage'));
const AddProgramPage = lazy(() => import('./pages/AddProgramPage'));
const ProgramDetailPage = lazy(() => import('./pages/ProgramDetailPage'));
const ProgramAnalysisPage = lazy(() => import('./pages/ProgramAnalysisPage'));
const ProgramComparePage = lazy(() => import('./pages/ProgramComparePage'));
const ProgramReviewPage = lazy(() => import('./pages/ProgramReviewPage'));

// Engine
const EngineDashboardPage = lazy(() => import('./pages/EngineDashboardPage'));
const EngineTrainingDayPage = lazy(() => import('./pages/EngineTrainingDayPage'));
const EngineTrainingDayReviewPage = lazy(() => import('./pages/EngineTrainingDayReviewPage'));
const EngineAnalyticsPage = lazy(() => import('./pages/EngineAnalyticsPage'));
const EngineLeaderboardPage = lazy(() => import('./pages/EngineLeaderboardPage'));
const EngineTaxonomyPage = lazy(() => import('./pages/EngineTaxonomyPage'));

// Nutrition
const NutritionDashboardPage = lazy(() => import('./pages/NutritionDashboardPage'));
const NutritionCalendarPage = lazy(() => import('./pages/NutritionCalendarPage'));

// AI Log
const AILogDashboardPage = lazy(() => import('./pages/AILogDashboardPage'));
const AILogUploadPage = lazy(() => import('./pages/AILogUploadPage'));
const AILogProgramPage = lazy(() => import('./pages/AILogProgramPage'));

// Public Q&A library (both trees — visitors and members)
const QALibraryPage = lazy(() => import('./pages/QALibraryPage'));
const KnowledgeListPage = lazy(() => import('./pages/KnowledgeListPage'));
const KnowledgeEntryPage = lazy(() => import('./pages/KnowledgeEntryPage'));
const StayPage = lazy(() => import('./pages/StayPage'));
const QAEntryPage = lazy(() => import('./pages/QAEntryPage'));
// Public examples/proof page (both trees — visitors and members)
const ExamplesPage = lazy(() => import('./pages/ExamplesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));

// Feature landing pages
const FeaturesHubPage = lazy(() => import('./pages/features/FeaturesHubPage'));
const AICoachingFeaturePage = lazy(() => import('./pages/features/AICoachingFeaturePage'));
const ResearchFeaturePage = lazy(() => import('./pages/features/ResearchFeaturePage'));
const ProgramsFeaturePage = lazy(() => import('./pages/features/ProgramsFeaturePage'));
const EngineFeaturePage = lazy(() => import('./pages/features/EngineFeaturePage'));
const NutritionFeaturePage = lazy(() => import('./pages/features/NutritionFeaturePage'));

function PageLoader() {
  return <div className="loading-screen"><div className="loading-pulse" /></div>;
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Which user we've already run the once-per-session bootstrap for. A normal
  // load resolves getSession() AND fires onAuthStateChange('SIGNED_IN'), so
  // without this every load fired the claim RPC and the timezone PATCH twice,
  // concurrently. A ref (not a local) so React's dev-mode double-invoke of this
  // effect doesn't reintroduce the duplicate.
  const bootstrappedFor = useRef<string | null>(null);

  useEffect(() => {
    // First-touch acquisition capture (UTM/referrer → localStorage → signup
    // metadata). Runs every load; first touch wins inside.
    captureAcquisition();

    const syncTimezone = (userId: string) => {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!tz) return;
      supabase.from('profiles').update({ timezone: tz })
        .eq('id', userId).neq('timezone', tz)
        .then(() => {}, () => {});
    };

    // Once-per-session side effects for a logged-in user. Safe to call from
    // both auth paths; only the first call for a given user does anything.
    const bootstrapSession = (session: Session) => {
      if (bootstrappedFor.current === session.user.id) return;
      bootstrappedFor.current = session.user.id;
      // Safety net: claim any pending subscription that matches this user's
      // email. No-op if nothing to claim. Backs up the
      // claim_pending_subscription trigger.
      supabase.rpc('claim_my_pending_subscription').then(() => {}, () => {});
      syncTimezone(session.user.id);
      // PWA install stamp: standalone display-mode means the app is
      // running from the home screen (covers iOS manual installs, where
      // no appinstalled event exists). Idempotent — first install wins.
      if (window.matchMedia('(display-mode: standalone)').matches) {
        supabase.rpc('mark_pwa_installed').then(() => {}, () => {});
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (session) bootstrapSession(session);
    }).catch(() => {
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === 'PASSWORD_RECOVERY') {
        window.location.href = '/settings';
      }
      if (event === 'SIGNED_IN' && session) bootstrapSession(session);
      // A different user signing in must be able to bootstrap again.
      if (event === 'SIGNED_OUT') bootstrappedFor.current = null;
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <PageLoader />;
  // Both trees get the provider: /checkout/complete renders signed-out as well
  // as signed-in, and it needs refresh() in the signed-in case. With no user the
  // provider holds an empty set and refresh() is a no-op.
  if (!session) return (
    <EntitlementsProvider userId={undefined}>
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/qa" element={<QALibraryPage />} />
            <Route path="/knowledge" element={<KnowledgeListPage />} />
            <Route path="/knowledge/:slug" element={<KnowledgeEntryPage />} />
            <Route path="/qa/:slug" element={<QAEntryPage />} />
            <Route path="/examples" element={<ExamplesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/stay" element={<StayPage />} />
            <Route path="/features" element={<FeaturesHubPage />} />
            <Route path="/features/coaching" element={<AICoachingFeaturePage />} />
            <Route path="/features/research" element={<ResearchFeaturePage />} />
            <Route path="/features/programs" element={<ProgramsFeaturePage />} />
            <Route path="/features/engine" element={<EngineFeaturePage />} />
            <Route path="/features/nutrition" element={<NutritionFeaturePage />} />
            <Route path="/checkout/complete" element={<CheckoutCompletePage />} />
            {/* Signed-out /checkout (email links, bookmarks): with a valid
                ?plan= it goes straight to Stripe via the anonymous
                create-checkout flow; otherwise it lands on the features hub —
                never silently on the landing page (the July '26
                recovery-email leak). */}
            <Route path="/checkout" element={<CheckoutRedirectPage />} />
            {/* Signed-out /profile (email "re-read your evaluation" links):
                sign in first, then land on the profile — never fall to the
                landing page. */}
            <Route path="/profile" element={<Navigate to="/auth?next=/profile" replace />} />
            {/* Dead-link stubs for the retired gym flows (Decisions 11 / 12a). */}
            <Route path="/join/engine/:token" element={<RetiredInviteNotice />} />
            <Route path="/claim/:token" element={<RetiredInviteNotice />} />
            <Route path="*" element={<LandingPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </EntitlementsProvider>
  );

  return (
    <EntitlementsProvider userId={session.user.id}>
      <AuthenticatedApp session={session} />
    </EntitlementsProvider>
  );
}

const HIDE_TAB_BAR_ROUTES = ['/workout/start', '/checkout', '/checkout/complete', '/welcome'];

function AuthenticatedApp({ session }: { session: Session }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Stage D: page_view per route change (pattern only, ids stripped).
  useEffect(() => {
    track('page_view', { path: routePattern(location.pathname) });
  }, [location.pathname]);

  // Welcome gate (2026-10-08): a brand-new account's first authenticated
  // load routes to /welcome — once per ACCOUNT (profiles.welcome_seen_at,
  // backfilled for everyone pre-existing), checked once per session
  // (sessionStorage). An existing evaluation also counts as oriented, so a
  // failed flag write can never trap a veteran. Checkout routes are left
  // alone — never yank a user mid-payment; they land on "/" after and the
  // gate catches them there.
  const welcomeCheckedFor = useRef<string | null>(null);
  useEffect(() => {
    const uid = session.user.id;
    if (welcomeCheckedFor.current === uid) return;
    const path = location.pathname;
    if (path === '/welcome' || path.startsWith('/checkout')) return;
    try {
      if (sessionStorage.getItem(`welcome-done:${uid}`) === '1') {
        welcomeCheckedFor.current = uid;
        return;
      }
    } catch { /* storage unavailable — fall through to the DB check */ }
    welcomeCheckedFor.current = uid;
    (async () => {
      try {
        const [{ data: prof }, evalRes] = await Promise.all([
          supabase.from('profiles').select('welcome_seen_at').eq('id', uid).maybeSingle(),
          supabase.from('profile_evaluations').select('id', { count: 'exact', head: true }).eq('user_id', uid),
        ]);
        const oriented = !!prof?.welcome_seen_at || (evalRes.count ?? 0) > 0;
        if (oriented) {
          try { sessionStorage.setItem(`welcome-done:${uid}`, '1'); } catch { /* ignore */ }
          return;
        }
        navigate('/welcome', { replace: true });
      } catch { /* soft-fail: never block the app on the gate */ }
    })();
  }, [session.user.id, location.pathname, navigate]);
  const hideTabBar = HIDE_TAB_BAR_ROUTES.some(r => location.pathname === r) ||
    location.pathname.startsWith('/features') ||
    location.pathname.startsWith('/qa') ||
    location.pathname.startsWith('/examples') ||
    location.pathname.startsWith('/stay');

  return (
    <>
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage session={session} />} />
            <Route path="/welcome" element={<WelcomePage session={session} />} />
            <Route path="/join/engine/:token" element={<RetiredInviteNotice />} />
            <Route path="/claim/:token" element={<RetiredInviteNotice />} />
            {/* REMOVED (Decision 11): the /gym, /gym/leaderboard, /tv/:token class surfaces are deleted. */}
            <Route path="/qa" element={<QALibraryPage signedIn />} />
            <Route path="/knowledge" element={<KnowledgeListPage signedIn />} />
            <Route path="/knowledge/:slug" element={<KnowledgeEntryPage signedIn />} />
            <Route path="/qa/:slug" element={<QAEntryPage signedIn />} />
            <Route path="/examples" element={<ExamplesPage signedIn />} />
            <Route path="/pricing" element={<PricingPage signedIn />} />
            <Route path="/stay" element={<StayPage />} />
            <Route path="/chat" element={<ChatPage session={session} />} />
            <Route path="/workout-review" element={<WorkoutReviewPage session={session} />} />
            <Route path="/workout/start" element={<StartWorkoutPage session={session} />} />
            <Route path="/day/:workoutId" element={<DayPage session={session} />} />
            <Route path="/workout-analysis" element={<WorkoutAnalysisPage session={session} />} />
            <Route path="/training-log" element={<TrainingLogPage session={session} />} />
            <Route path="/log-activity" element={<LogActivityPage session={session} />} />
            <Route path="/programs" element={<ProgramsListPage session={session} />} />
            <Route path="/programs/new" element={<AddProgramPage session={session} />} />
            <Route path="/programs/:id/analyze" element={<ProgramAnalysisPage session={session} />} />
            <Route path="/programs/:id/modify/:modificationId/compare" element={<ProgramComparePage session={session} />} />
            <Route path="/programs/:id/modify/:modificationId/review" element={<ProgramReviewPage session={session} />} />
            <Route path="/programs/:id" element={<ProgramDetailPage session={session} />} />
            <Route path="/checkout" element={<CheckoutPage session={session} />} />
            <Route path="/checkout/complete" element={<CheckoutCompletePage />} />
            <Route path="/history" element={<HistoryPage session={session} />} />
            <Route path="/bookmarks" element={<BookmarksPage session={session} />} />
            <Route path="/settings" element={<SettingsPage session={session} />} />
            <Route path="/profile" element={<AthletePage session={session} />} />
            <Route path="/athletedata" element={<AthleteDataPage session={session} />} />
            <Route path="/admin" element={<AdminPage session={session} />} />
            <Route path="/admin/ratings" element={<AdminRatingsPage session={session} />} />
            <Route path="/admin/reports" element={<AdminReportsPage session={session} />} />
            <Route path="/admin/activity" element={<AdminActivityFeedPage session={session} />} />
            <Route path="/admin/ops" element={<AdminOpsPage session={session} />} />
            <Route path="/admin/research" element={<AdminResearchPage session={session} />} />
            <Route path="/admin/program-score" element={<AdminProgramScorePage session={session} />} />
            <Route path="/admin/signatures" element={<AdminSignaturesPage session={session} />} />
            <Route path="/admin/outreach" element={<AdminOutreachPage session={session} />} />
            <Route path="/admin/users/:id" element={<AdminUserDetailPage session={session} />} />
            <Route path="/admin/users/:id/timeline" element={<AdminUserTimelinePage session={session} />} />
            <Route path="/admin/users/:id/athlete-profile" element={<AdminAthleteProfilePage session={session} />} />
            <Route path="/admin/users/:id/athlete-model" element={<AdminAthleteModelPage session={session} />} />
            <Route path="/admin/users/:id/evaluations" element={<AdminEvaluationsPage session={session} />} />
            <Route path="/admin/users/:id/evaluations/:evalType/:evalId" element={<AdminEvaluationsPage session={session} />} />
            <Route path="/admin/users/:id/engine-sessions" element={<AdminEngineSessionsPage session={session} />} />
            <Route path="/admin/users/:id/engine-sessions/:sessionId" element={<AdminEngineSessionDetailPage session={session} />} />
            <Route path="/admin/users/:id/engine-resequence" element={<AdminEngineResequencePage session={session} />} />
            <Route path="/admin/users/:id/chat" element={<AdminChatPage session={session} />} />
            <Route path="/admin/users/:id/workouts" element={<AdminWorkoutLogsPage session={session} />} />
            <Route path="/admin/users/:id/workouts/:workoutId" element={<AdminWorkoutLogDetailPage session={session} />} />
            <Route path="/admin/users/:id/programs" element={<AdminProgramsPage session={session} />} />
            <Route path="/admin/users/:id/programs/:programId" element={<AdminProgramsPage session={session} />} />
            <Route path="/admin/users/:id/nutrition" element={<AdminNutritionPage session={session} />} />
            <Route path="/engine" element={<EngineDashboardPage session={session} />} />
            <Route path="/engine/dashboard" element={<EngineDashboardPage session={session} />} />
            <Route path="/engine/training/:dayNumber" element={<EngineTrainingDayPage session={session} />} />
            <Route path="/engine/training/:dayNumber/review" element={<EngineTrainingDayReviewPage session={session} />} />
            <Route path="/engine/analytics" element={<EngineAnalyticsPage session={session} />} />
            <Route path="/engine/leaderboard" element={<EngineLeaderboardPage session={session} />} />
            <Route path="/engine/taxonomy" element={<EngineTaxonomyPage session={session} />} />
            <Route path="/nutrition" element={<NutritionDashboardPage session={session} />} />
            <Route path="/nutrition/calendar" element={<NutritionCalendarPage session={session} />} />
            <Route path="/ailog" element={<AILogDashboardPage session={session} />} />
            <Route path="/ailog/upload" element={<AILogUploadPage session={session} />} />
            <Route path="/ailog/:id" element={<AILogProgramPage session={session} />} />
            <Route path="/features" element={<FeaturesHubPage />} />
            <Route path="/features/coaching" element={<AICoachingFeaturePage />} />
            <Route path="/features/research" element={<ResearchFeaturePage />} />
            <Route path="/features/programs" element={<ProgramsFeaturePage signedIn />} />
            <Route path="/features/engine" element={<EngineFeaturePage signedIn />} />
            <Route path="/features/nutrition" element={<NutritionFeaturePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      {!hideTabBar && <BottomTabBar />}
      <InstallPrompt />
    </>
  );
}
